// Derived stats from a sampled payoff curve. Pure functions, no IO.

import {
  perpPnl,
  predPnl,
  type PayoffPoint,
  type PerpLeg,
  type PredictionLeg,
} from "./composePayoff";

/**
 * Find all zero crossings of the total P&L curve over a sampled series.
 * Linear interpolation between each pair of consecutive samples of opposite
 * sign. Exact zeros are kept as-is. Near-duplicates (within 1e-3 USD) dedupe.
 */
export function findBreakevens(
  points: ReadonlyArray<PayoffPoint>,
): number[] {
  const out: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    if (a.totalPnl === 0) {
      out.push(a.price);
      continue;
    }
    if (a.totalPnl * b.totalPnl < 0) {
      const t = a.totalPnl / (a.totalPnl - b.totalPnl);
      out.push(a.price + t * (b.price - a.price));
    }
  }
  if (points.length > 0) {
    const last = points[points.length - 1]!;
    if (last.totalPnl === 0) out.push(last.price);
  }
  out.sort((x, y) => x - y);
  return out.filter((v, i) => i === 0 || Math.abs(v - out[i - 1]!) > 1e-3);
}

/** Total P&L (perp + pred) at an arbitrary price, not snapped to samples. */
export function totalPnlAt(
  perp: PerpLeg,
  pred: PredictionLeg,
  price: number,
): number {
  return perpPnl(perp, price) + predPnl(pred, price);
}

export type Scenario = {
  label: string;
  price: number;
  perpPnl: number;
  predPnl: number;
  totalPnl: number;
  /** Optional hint color class for labels (e.g. 'crit' for liq). */
  tone?: "mark" | "threshold" | "liq" | "gain" | "loss" | "neutral";
};

/**
 * A curated scenarios list: current mark, threshold kink, liquidation (if
 * within a sensible window), and ±5/±10% off mark. Always sorted by price.
 */
export function buildScenarios(
  perp: PerpLeg,
  pred: PredictionLeg,
  mark: number,
): Scenario[] {
  const rows: Scenario[] = [];

  const push = (label: string, price: number, tone?: Scenario["tone"]) => {
    const pPerp = perpPnl(perp, price);
    const pPred = predPnl(pred, price);
    rows.push({
      label,
      price,
      perpPnl: pPerp,
      predPnl: pPred,
      totalPnl: pPerp + pPred,
      tone,
    });
  };

  // Push informative/named labels first so dedup keeps them over the
  // percentage-off-mark bands they might coincide with (e.g. liq at −10%).
  push("MARK", mark, "mark");
  push("THRESHOLD", pred.threshold, "threshold");
  // Liq is meaningful only if it falls within a reasonable viewing window
  // (±30% of mark). Otherwise the floor is already visible from the ±10%
  // samples and adding liq just adds noise.
  if (perp.liquidation > mark * 0.7 && perp.liquidation < mark * 1.3) {
    push("LIQ", perp.liquidation, "liq");
  }
  push("−10%", mark * 0.9, "loss");
  push("−5%", mark * 0.95, "loss");
  push("+5%", mark * 1.05, "gain");
  push("+10%", mark * 1.1, "gain");

  // Dedupe prices that coincide (e.g. threshold == mark), keeping the
  // earlier-pushed (more informative) label.
  const seen = new Set<number>();
  const deduped: Scenario[] = [];
  for (const r of rows) {
    const key = Math.round(r.price * 100);
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(r);
  }

  deduped.sort((a, b) => a.price - b.price);
  return deduped;
}

/**
 * Simple strategy classification for the header badge. Purely qualitative —
 * not a risk metric.
 */
export function classifyStrategy(
  perp: PerpLeg,
  pred: PredictionLeg,
): string {
  const perpBull = perp.side === "long";
  const yesOnUp =
    (pred.outcome === "yes" && pred.direction === "above") ||
    (pred.outcome === "no" && pred.direction === "below");
  const yesOnDown =
    (pred.outcome === "yes" && pred.direction === "below") ||
    (pred.outcome === "no" && pred.direction === "above");

  if (perpBull && yesOnUp) return "STACKED BULL";
  if (!perpBull && yesOnDown) return "STACKED BEAR";
  if (perpBull && yesOnDown) return "HEDGED BULL";
  if (!perpBull && yesOnUp) return "HEDGED BEAR";
  return "COMPOSITE";
}
